import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDataUser, setTemporalUsers } from "../libs/redux/slices/authSlice/authSlice";
import { useGetUserTemporalQuery, useGetTurnsTodayByUserQuery } from "../libs/redux/slices/authSlice/authApiSlice";
import { BoxStatus, ShiftBanner, Box } from "codekit";
import { Avisoxxx, useDrilldown } from "../components/Tablerox";
import { setInitialFilter } from "../libs/redux/slices/Graficsx/Criticid/criticidSlice";
import { useGetQuantityCriticalQuery } from "../libs/redux/slices/Graficsx/Criticid/criticidApiSlice";
import { Buttonsx, Galerdas } from "../components/Dashboax";
import { useGetVisiblesQuery } from "../libs/redux/slices/Paramgen/Dashestx/dashestxApiSlice";
import { useGetMipuestoMiCargaQuery, useGetMipuestoMiTurnoQuery, useGetMipuestoWidgetsQuery } from "../libs/redux/slices/Mipuesto/mipuestoApiSlice";
import Midiaxxx from "../components/Mipuesto/Midiaxxx";
import Ventanax from "../components/Tablerox/Ventanax";
import Cargaeje from "../components/Estructu/Cargaeje";

/**
 * Mi Puesto de Trabajo = la pantalla de entrada (Diego 2026-09-16): el Inicio y
 * Mi Puesto eran dos versiones de lo mismo. Aquí queda una sola: la cabecera del
 * puesto, el aviso de operaciones críticas, "Mi día" (qué hacer hoy) y el
 * escritorio (plantilla del cargo fija + tableros adicionales con permiso). La
 * única parametría de tableros por cargo es la plantilla de escritorio
 * (CIMS0120); la lista del Inicio (CIMS0037) se retiró. Los tableros estáticos
 * (CIMS0118) siguen en su pestaña.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-16
 */
function DashboardHome() {
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState("HOME");
  const { USRIDXXX, USRNOMXX } = useSelector(getDataUser);
  //Los tableros estaticos del cargo (CIMS0118). RTK Query cachea la respuesta, asi que Galerdas
  //reusa esta misma consulta y no se pide dos veces.
  const { data: visibles, isLoading: isLoadingVisibles } = useGetVisiblesQuery();
  const { data: turnsByUser } = useGetTurnsTodayByUserQuery({ USRIDXXX });
  const { data: temporalUsers, isSuccess } = useGetUserTemporalQuery({ USRIDXXX });
  //Solo para la cabecera (nombre del cargo); el escritorio lo pinta Miescrix
  const { data: escritorio } = useGetMipuestoWidgetsQuery();
  //Carga del Ejecutivo de Cuenta (Diego 2026-10-01): puntos de sus operaciones vivas, con peso por etapa, frente a su tope
  const { data: miCarga } = useGetMipuestoMiCargaQuery();
  const carga = miCarga?.data ?? null;
  const [verCarga, setVerCarga] = useState(false);
  //Turno del Ejecutivo (Diego 2026-10-01): al entrar a su turno debe tener claro qué operaciones le corresponden
  //Se vuelve a consultar cada minuto: el aviso de fin de turno depende de la hora
  const { data: miTurno } = useGetMipuestoMiTurnoQuery(undefined, { pollingInterval: 60000 });
  const turno = miTurno?.data ?? null;
  //Últimos 30 minutos del turno: se avisa para que alcance a dejar al día lo que entrega
  const porTerminar = Boolean(turno?.EN_TURNO) && Number(turno?.FALTAN ?? 999) <= 30;
  const puntos = (n) => Number(n ?? 0).toLocaleString("es-CO", { maximumFractionDigits: 2 });
  const cargoDes = String(escritorio?.data?.CARGODES ?? "");
  const [banner, setBanner] = React.useState(null);
  //El aviso de operaciones críticas se cierra por sesión y por cantidad: si cambia el número, vuelve a salir
  const [avisoCerrado, setAvisoCerrado] = useState(() => window.sessionStorage.getItem("aviso-criticas-cerrado"));
  const { ir, filtroIn } = useDrilldown();
  const [isCritical, setIsCritical] = useState(false)
  const { data: criticalOperations, isSuccess: isSuiccessCritical } = useGetQuantityCriticalQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );
  const [quantityCritical, setQuantityCritical] = useState(0)

  const toDateAt = (dateStr, timeStr) => new Date(`${dateStr}T${timeStr}`);

  const fmtHour = (d) =>
    d.toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit', hour12: true });


  //Hook para el seteo de los usuarios reemplazo
  useEffect(() => {
    if (isSuccess) {
      const { data = [] } = temporalUsers;
      dispatch(setTemporalUsers(data));
    }
  }, [isSuccess, temporalUsers, dispatch]);

  //Hook para validar si se encontraron operaciones criticas
  useEffect(() => {
    if (isSuiccessCritical) {
      const { data = {} } = criticalOperations;
      if (Object.keys(data).length > 0) {
        setIsCritical(true)
        setQuantityCritical(data["ROJO"].total)
      }
    }
  }, [isSuiccessCritical, criticalOperations])

  React.useEffect(() => {
    const compute = () => {
      const dayData = turnsByUser?.data;
      const turns = dayData?.TURNSXXX ?? [];

      if (!Array.isArray(turns) || turns.length === 0) {
        setBanner({ visible: false, variant: 'start', primary: '', secondary: '' });
        return;
      }

      const dateStr = dayData.DATEXXXX;

      const ranges = turns
        .flatMap(t => (t.RANGESXX ?? []).map(r => ({
          start: toDateAt(dateStr, r.STARTXXX),
          end: toDateAt(dateStr, r.ENDXXXXX),
        })))
        .sort((a, b) => a.start - b.start);

      const now = new Date();

      const current = ranges.find(r => now >= r.start && now < r.end);
      if (current) {
        setBanner({
          visible: true,
          variant: 'end',
          primary: `Tu turno termina a las ${fmtHour(current.end)}`,
          secondary: 'Recuerda finalizar tu turno en la opción Cerrar Turno',
        });
        return;
      }

      const next = ranges.find(r => now < r.start);
      if (next) {
        setBanner({
          visible: true,
          variant: 'start',
          primary: `Tu turno inicia a las ${fmtHour(next.start)}`,
          secondary: `Hoy (${dayData.DAYXXXXX}). Recuerda finalizar tu turno en la opción Cerrar Turno`,
        });
        return;
      }

      setBanner({ visible: false, variant: 'start', primary: '', secondary: '' });
    };

    compute();
    const id = setInterval(compute, 5 * 60 * 1000); // 5 minutos
    return () => clearInterval(id);
  }, [turnsByUser]);

  //Se espera a los tableros estaticos para que la barra de pestañas no aparezca a medias
  if (isLoadingVisibles) {
    return <BoxStatus variant="loading" text="Cargando información..." />;
  }

  //La pestaña TABLEROS (estaticos, CIMS0118) solo aparece si el cargo tiene alguno
  const hasTableros = (visibles?.data?.length ?? 0) > 0;
  const currentTab = hasTableros ? currentView : "HOME";

  //Aviso de operaciones críticas (kit): al tablero de criticidad si está en el escritorio, o al tracking
  const handleVerTablero = (color) => {
    dispatch(setInitialFilter(color));
    const nodo = document.getElementById("widget-113");
    if (!nodo) {
      handleAbrirTracking(color);
      return;
    }
    setTimeout(() => nodo.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };
  const fechaHoy = new Date().toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" });
  const handleAbrirTracking = (color) => ir("Modimpxx", "Trackimp", [filtroIn("MDOSYYYY.REGESTXX", "ACTIVO"), filtroIn("MDSSYYYY.COLORXXX", color)]);
  const handleCerrarAviso = () => {
    window.sessionStorage.setItem("aviso-criticas-cerrado", String(quantityCritical));
    setAvisoCerrado(String(quantityCritical));
  };

  return (
    <>
      {/**Operaciones críticas: aviso del kit en vez del modal que tapaba la pantalla (Diego 2026-09-14) */}
      {isCritical && quantityCritical > 0 && avisoCerrado !== String(quantityCritical) && (
        <Box mb={4}>
          <Avisoxxx
            tonoxxx="ROJO"
            kicker={`Bienvenid@ ${USRNOMXX}`}
            valor={quantityCritical}
            titulo={quantityCritical === 1 ? "operación crítica en tu perfil" : "operaciones críticas en tu perfil"}
            texto="Ya incumplen o están al borde de sus términos. Cada número abre su pantalla."
            acciones={[
              { clave: "tracking", texto: "Abrir en el tracking", primaria: true, onClick: () => handleAbrirTracking("ROJO") },
              { clave: "tablero", texto: "Ver en el tablero", onClick: () => handleVerTablero("ROJO") },
            ]}
            onCerrar={handleCerrarAviso}
          />
        </Box>
      )}
      {/* Cabecera del puesto: mismo lenguaje visual de los Tableros */}
      <div style={{ background: "linear-gradient(90deg, #1f3864 0%, #2e5096 100%)", borderRadius: 12, padding: "18px 28px", color: "#fff", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
       <div>
        <div style={{ fontSize: 24, fontWeight: 700 }}>
          Mi Puesto de Trabajo
          {cargoDes && (
            <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 600, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 12, padding: "3px 12px", verticalAlign: "middle" }}>
              {cargoDes}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{USRNOMXX ?? ""} · {fechaHoy}</div>
       </div>
        {turno && (
          <button type="button" disabled={!turno.EN_TURNO || turno.RECIBIDAS.length === 0}
            onClick={() => ir("Modimpxx", "Planeimp", [filtroIn("MDOSYYYY.REGESTXX", "ACTIVO"), filtroIn("MDOSYYYY.DOCNROXX", turno.RECIBIDAS)])}
            title={porTerminar ? "Tu turno está por terminar: al cambiar el turno, tus operaciones de clientes por turnos pasan a quien entra. Clic para abrir las que recibiste en este turno." : turno.EN_TURNO ? "Operaciones que recibiste al entrar a este turno. Clic para abrirlas." : (turno.RECIBE ? "Tu turno terminó, pero todavía no ha entrado otro: sigues a cargo y sigues recibiendo operaciones hasta que entre el siguiente turno." : "No estás en turno: no recibes operaciones nuevas hasta tu próximo turno. Las operaciones de clientes por turnos que tenías pasaron a quien entró.")}
            style={{ textAlign: "right", color: "#fff", cursor: turno.EN_TURNO && turno.RECIBIDAS.length > 0 ? "pointer" : "default", fontFamily: "inherit", background: porTerminar ? "rgba(229,116,46,0.45)" : turno.EN_TURNO ? "rgba(46,160,90,0.3)" : "rgba(255,255,255,0.12)", border: `1px solid ${porTerminar ? "rgba(255,210,170,0.9)" : "rgba(255,255,255,0.3)"}`, borderRadius: 12, padding: "8px 16px", marginLeft: "auto" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.85 }}>{porTerminar ? `Tu turno termina en ${turno.FALTAN} min · a las ${String(turno.HASTA).slice(11, 16)}` : turno.EN_TURNO ? `En turno hasta las ${String(turno.HASTA).slice(11, 16)}${turno.PRORROGA ? " · con prórroga" : ""}` : "Fuera de turno"}</div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{turno.EN_TURNO ? turno.RECIBIDAS.length : "—"} <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>{turno.EN_TURNO ? "recibidas en este turno" : turno.RECIBE ? "sigues a cargo hasta que entre otro turno" : "no recibes operaciones nuevas"}</span></div>
          </button>
        )}
        {carga && (
          <button type="button" onClick={() => setVerCarga(true)} title="Puntos de tus operaciones vivas frente a tu tope. Clic para ver de dónde sale cada punto."
            style={{ textAlign: "right", color: "#fff", cursor: "pointer", fontFamily: "inherit", background: carga.COPADO ? "rgba(229,46,46,0.25)" : "rgba(255,255,255,0.12)", border: `1px solid ${carga.COPADO ? "rgba(255,180,180,0.7)" : "rgba(255,255,255,0.3)"}`, borderRadius: 12, padding: "8px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.85 }}>Mi carga · operaciones vivas</div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{puntos(carga.CARGA)} <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>de {puntos(carga.TOPE)} puntos{carga.COPADO ? " · copado" : ""}</span></div>
          </button>
        )}
      </div>
      <Ventanax isOpen={verCarga} onCerrar={() => setVerCarga(false)} ancho="media" hoja>
        {verCarga && <Cargaeje carga={carga} onCerrar={() => setVerCarga(false)}
          onAbrir={(DOCNROXX) => { setVerCarga(false); ir("Modimpxx", "Planeimp", [filtroIn("MDOSYYYY.REGESTXX", "ACTIVO"), filtroIn("MDOSYYYY.DOCNROXX", [DOCNROXX])]); }} />}
      </Ventanax>
      {hasTableros && <Buttonsx currentView={currentTab} handleCurrentView={setCurrentView} />}
      <Box style={{ display: currentTab === "HOME" ? "block" : "none" }}>
        {banner?.visible && (
          <Box spacing={5}>
            <ShiftBanner
              variant={banner.variant}
              srcBgImage="/images/cima-icon.png"
              primaryText={banner.primary}
              secondaryText={banner.secondary}
              alignment="left"
              imageRotation={-10}
              imageX={95}
            />
          </Box>
        )}
        <Midiaxxx />
      </Box>
      {hasTableros && currentTab === "TABLEROS" && <Galerdas />}
    </>
  );
}

export default DashboardHome;
